import {
    Box,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerOverlay,
    Flex,
    InputGroup,
    Select,
    Stack,
} from '@chakra-ui/react';
import { DeleteIcon, SunIcon } from '@chakra-ui/icons';
import usePipelineStore from '../../../stores/pipeline-store';
import useRuntimeStore from '../../../stores/runtime-store';
import ColorPicker from '../ColorPicker';
import { useCallback } from 'react';
import { DrawerAccordion } from './DrawerAccordion';
import { DrawerInput } from './inputs';
import { DrawerActionButton } from './DrawerActionButton';
import { createStepByType } from './stepFactories';

export function StepDrawer() {
    const { selectedStepId, setSelectedStepId } = useRuntimeStore((state) => state);
    const { pipeline, updateStep, setPipeline } = usePipelineStore((state) => state);
    const stepId = selectedStepId as string;
    const step = pipeline.steps[stepId] as PipelineStep;

    const setStepType = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value as PipelineStep['type'];
            const newPipeline = JSON.parse(JSON.stringify(pipeline)) as PipelineJson;
            newPipeline.steps[stepId] = createStepByType(value, step);
            setPipeline(newPipeline);
        },
        [pipeline, setPipeline, stepId, step],
    );

    const deleteStep = useCallback(() => {
        const stepsStr = JSON.stringify(pipeline.steps);
        const accurenceInSteps = stepsStr.split(stepId).length - 2;
        if (
            accurenceInSteps == 0 ||
            window.confirm(
                `This step is being referenced ${accurenceInSteps} times.\nAre you sure you want to delete it?`,
            ) == true
        ) {
            let newPipeline = JSON.parse(JSON.stringify(pipeline));
            const index = newPipeline.editorSettings.stepIds.indexOf(stepId);

            newPipeline.editorSettings.stepIds.splice(index, 1);
            newPipeline.steps[stepId] = undefined;
            if (pipeline.firstStepId == stepId) {
                newPipeline.firstStepId = '';
            }
            newPipeline.steps = JSON.parse(JSON.stringify(pipeline.steps).replaceAll(stepId, ''));

            setPipeline(newPipeline);
        }
    }, [pipeline, setPipeline, stepId]);

    const setAsFirstStep = useCallback(() => {
        let newPipeline = JSON.parse(JSON.stringify(pipeline));
        newPipeline.firstStepId = stepId;
        setPipeline(newPipeline);
    }, [pipeline, setPipeline, stepId]);

    if (!step) return null;

    return (
        <>
            <Drawer isOpen={!!selectedStepId} placement="right" onClose={() => setSelectedStepId('')} size={'xl'}>
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton
                        _focus={{ borderWidth: 0 }}
                        _active={{ borderWidth: 0 }}
                    />
                    <DrawerBody>
                        <Stack margin="auto" mt={50}>
                            <Box>
                                <InputGroup>
                                    <ColorPicker
                                        color={step.editorSettings.color}
                                        onColorChanged={(color: string) => {
                                            updateStep(stepId, 'color', color, 'editorSettings');
                                        }}
                                    />
                                    <Select ml={1} value={step.type} onChange={setStepType}>
                                        <option value="EMPTY">⛅️&nbsp;&nbsp;Empty Step</option>
                                        <option value="SEND_MESSAGE">📟&nbsp;&nbsp;Send Message</option>
                                        <option value="CREATE_SURVEY_LINK">🌐&nbsp;&nbsp;Create Survey Link</option>
                                        <option value="CREATE_DASHBOARD">🖥️&nbsp;&nbsp;Create Dashboard</option>
                                        <option value="GET_ANSWER_VALUES">📜&nbsp;&nbsp;Get Survey Answer</option>
                                        <option value="SET_VAR">📝&nbsp;&nbsp;Set Var</option>
                                        <option value="GENERATE_STORY">🍙&nbsp;&nbsp;Generate Story</option>
                                        <option value="MODERATOR_INPUT">👮&nbsp;&nbsp;Moderator Input</option>
                                        <option value="EXTERNAL_REQUEST">🌪&nbsp;&nbsp;External Request</option>
                                        <option value="FINISH">🌜&nbsp;&nbsp;Finish</option>
                                    </Select>
                                </InputGroup>
                                <InputGroup mt={3}>
                                    <DrawerInput
                                        value={step.name}
                                        onChange={(e) => updateStep(stepId, 'name', e.target.value)}
                                        placeholder="Step Name"
                                    />
                                </InputGroup>
                            </Box>
                            <Box>
                                <Flex>
                                    <DrawerActionButton leftIcon={<DeleteIcon />} onClick={deleteStep}>
                                        Delete Step
                                    </DrawerActionButton>
                                    {(pipeline as PipelineJson).firstStepId != stepId && (
                                        <DrawerActionButton leftIcon={<SunIcon />} onClick={setAsFirstStep}>
                                            Set as initial
                                        </DrawerActionButton>
                                    )}
                                </Flex>
                            </Box>
                            <DrawerAccordion stepId={stepId} />
                        </Stack>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    );
}
